'use client';

import { useState } from 'react';
import { User, Mail, Send, AlertCircle } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { CountrySelect } from '@/components/frontend/common/country-select';
import { PhoneInput } from '@/components/frontend/common/phone-input';
import { countryDialCodes } from '@/lib/data/country-dial-codes';

interface PricingFormProps {
    onSubmit: (data: { name: string; email: string; country: string; contact: string }) => Promise<void>;
    isLoading: boolean;
    error: string | null;
}

export function PricingForm({ onSubmit, isLoading, error }: PricingFormProps) {
    const t = useTranslations('pricing');
    const [form, setForm] = useState({
        name: '',
        phone: '',
        email: '',
        country: '',
    });
    const [countryCode, setCountryCode] = useState<string>('');
    const [phoneFieldError, setPhoneFieldError] = useState<string>('');

    const validatePhone = (phone: string): boolean => {
        if (!phone || phone.trim() === '') return false;
        const cleaned = phone.replace(/[\s\-\(\)\+]/g, '');
        return /^\d{7,15}$/.test(cleaned);
    };

    const isFormValid =
        form.name.trim().length > 1 &&
        form.phone.trim().length > 0 &&
        validatePhone(form.phone) &&
        /.+@.+\..+/.test(form.email.trim()) &&
        form.country.trim().length > 0 &&
        form.country !== 'Select a country';

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;

        if (name === 'country') {
            setForm({ ...form, country: value });
            const newCode = countryDialCodes[value] || '';
            setCountryCode(newCode);
            setForm((prev) => ({ ...prev, phone: '' }));
            setPhoneFieldError('');
            return;
        }

        if (name === 'phone') {
            if (!countryCode || !form.country) {
                setPhoneFieldError(t('form.errors.selectCountryFirst'));
                return;
            }
            setPhoneFieldError('');
            setForm({ ...form, phone: value });
            return;
        }

        setForm({ ...form, [name]: value });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!form.country || form.country === '') {
            return;
        }

        if (!form.phone || !validatePhone(form.phone)) {
            return;
        }

        if (!isFormValid || isLoading) {
            return;
        }

        await onSubmit({
            name: form.name.trim(),
            email: form.email.trim(),
            country: form.country.trim(),
            contact: countryCode ? `${countryCode} ${form.phone}`.trim() : form.phone,
        });
    };

    return (
        <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl dark:border-gray-800 dark:bg-gray-900">
            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {t('form.title') || 'Get Pricing Access'}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                        {t('form.description') || 'Enter your details to receive a one-time verification code via email.'}
                    </p>
                </div>

                {error && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/60 dark:bg-red-500/10 dark:text-red-200">
                        <div className="flex items-start gap-2">
                            <AlertCircle className="mt-0.5 h-5 w-5" />
                            <span>{error}</span>
                        </div>
                    </div>
                )}

                {/* Name Field */}
                <div className="group relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
                        <User className="w-5 h-5 text-gray-400 dark:text-gray-500 group-focus-within:text-primary-500 transition-colors duration-300" />
                    </div>
                    <input
                        type="text"
                        id="pricing-name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-4 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border-2 border-gray-200/50 dark:border-gray-700/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 dark:focus:border-primary-400 focus:bg-white dark:focus:bg-gray-900 focus:shadow-lg focus:shadow-primary-500/20 focus:outline-none transition-all duration-300"
                        placeholder={t('form.namePlaceholder') || 'Your Full Name'}
                        required
                    />
                    <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-primary-500/0 via-primary-500/0 to-primary-500/0 group-focus-within:from-primary-500/10 group-focus-within:via-primary-500/5 group-focus-within:to-primary-500/10 transition-all duration-300 pointer-events-none" />
                </div>

                {/* Email Field */}
                <div className="group relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 z-10">
                        <Mail className="w-5 h-5 text-gray-400 dark:text-gray-500 group-focus-within:text-primary-500 transition-colors duration-300" />
                    </div>
                    <input
                        type="email"
                        id="pricing-email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        className="w-full pl-12 pr-4 py-4 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm border-2 border-gray-200/50 dark:border-gray-700/50 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-primary-500 dark:focus:border-primary-400 focus:bg-white dark:focus:bg-gray-900 focus:shadow-lg focus:shadow-primary-500/20 focus:outline-none transition-all duration-300"
                        placeholder={t('form.emailPlaceholder') || 'Your Business Email'}
                        required
                    />
                    <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-primary-500/0 via-primary-500/0 to-primary-500/0 group-focus-within:from-primary-500/10 group-focus-within:via-primary-500/5 group-focus-within:to-primary-500/10 transition-all duration-300 pointer-events-none" />
                </div>

                {/* Country Field */}
                <CountrySelect
                    id="pricing-country"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    placeholder={t('form.selectCountry')}
                    required
                />

                {/* Phone Field */}
                <PhoneInput
                    id="pricing-phone"
                    name="phone"
                    value={form.phone}
                    countryCode={countryCode}
                    country={form.country}
                    onChange={handleChange}
                    error={phoneFieldError}
                    placeholder={t('form.phonePlaceholder')}
                    required
                />

                {/* Submit Button */}
                <button
                    type="submit"
                    disabled={!isFormValid || isLoading}
                    className="group relative w-full px-8 py-4 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-600 hover:from-blue-700 hover:via-blue-600 hover:to-blue-700 disabled:from-gray-400 disabled:via-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition-all duration-300 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 hover:scale-[1.02] active:scale-[0.98] overflow-hidden"
                >
                    <span className="relative z-10 flex items-center justify-center gap-2">
                        <Send className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                        {isLoading ? (t('form.sending') || 'Sending code...') : (t('form.submit') || 'Send verification code')}
                    </span>
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                </button>
                <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                    {t('form.privacy') || 'We use this information to tailor follow-up conversations.'}
                </p>
            </form>
        </div>
    );
}

